import ApiService from './apiService'

/**
 * Service d'authentification pour gérer tout ce qui concerne l'auth utilisateur
 */
class AuthService {
    /**
     * Inscrit un nouvel utilisateur
     * @param {Object} userData Données d'inscription (nom, email, password, etc.)
     * @returns {Promise} Réponse API
     */
    register(userData) {
        return ApiService.post('/auth/register', {
            name: userData.name,
            email: userData.email,
            password: userData.password,
            password_confirmation: userData.password_confirmation,
            company_name: userData.company_name,
            business_type: userData.business_type || ''
        })
    }

    /**
     * Connecte un utilisateur
     * @param {Object} credentials Identifiants (email, password)
     * @returns {Promise} Réponse API avec token
     */
    login(credentials) {
        return ApiService.post('/auth/login', {
            email: credentials.email,
            phone: credentials.phone,
            password: credentials.password,
            device_id: this.getDeviceId(),
            device_name: navigator.userAgent || 'Web Browser'
        })
    }

    /**
     * Déconnecte l'utilisateur actuel
     * @returns {Promise} Réponse API
     */
    logout() {
        return localStorage.clear();
        // return ApiService.post('/auth/logout')
    }

    /**
     * Récupère les informations de l'utilisateur connecté
     * @returns {Promise} Réponse API avec données utilisateur
     */
    getUser() {
        const user = localStorage.getItem('user');
        if (user) {
            return Promise.resolve(JSON.parse(user));
        }
        return ApiService.get('/auth/me').then(response => {
            localStorage.setItem('user', JSON.stringify(response.data));
            return response.data;
        });
    }

    /**
     * Renvoie l'email de vérification
     * @param {String} email Email de l'utilisateur
     * @returns {Promise} Réponse API
     */
    resendVerification(email) {
        return ApiService.post('/auth/email/verification-notification', { email })
    }

    /**
     * Vérifie l'email avec les paramètres du lien de vérification
     * @param {Object} params Paramètres de vérification (id, hash, expires, signature)
     * @returns {Promise} Réponse API
     */
    verifyEmail(params) {
        return ApiService.get(`/email/verify/${params.id}/${params.hash}?expires=${params.expires}&signature=${params.signature}`)
    }

    /**
     * Initialise le processus de réinitialisation du mot de passe
     * @param {String} email Email de l'utilisateur
     * @returns {Promise} Réponse API
     */
    forgotPassword(email) {
        return ApiService.post('/auth/forgot-password', { email })
    }

    /**
     * Réinitialise le mot de passe avec le token
     * @param {Object} resetData Données de réinitialisation
     * @returns {Promise} Réponse API
     */
    requestResetPasswordLink(email) {
        return ApiService.post('/auth/request-reset-password-link', { email })
    }

    /**
     * Réinitialise le mot de passe avec le token
     * @param {Object} resetData Données de réinitialisation
     * @returns {Promise} Réponse API
     */
    resetPassword(resetData) {
        return ApiService.post('/auth/reset-password', {
            token: resetData.token,
            email: resetData.email,
            password: resetData.password,
            password_confirmation: resetData.password_confirmation
        })
    }

    /**
     * Met à jour le profil de l'utilisateur
     * @param {Object} profileData Données du profil
     * @returns {Promise} Réponse API
     */
    updateProfile(profileData) {
        return ApiService.put('/auth/profile', profileData)
    }

    /**
     * Change le mot de passe de l'utilisateur connecté
     * @param {Object} passwordData Données de changement de mot de passe
     * @returns {Promise} Réponse API
     */
    changePassword(passwordData) {
        return ApiService.put('/auth/password', {
            current_password: passwordData.current_password,
            password: passwordData.password,
            password_confirmation: passwordData.password_confirmation
        })
    }

    /**
     * Génère ou récupère l'ID de l'appareil utilisé
     * @returns {String} ID de l'appareil
     */
    getDeviceId() {
        let deviceId = localStorage.getItem('device_id')

        if (!deviceId) {
            // Générer un ID d'appareil unique
            deviceId = 'web-' +
                new Date().getTime() + '-' +
                Math.random().toString(36).substring(2, 15) +
                Math.random().toString(36).substring(2, 15)
            localStorage.setItem('device_id', deviceId)
        }

        return deviceId
    }

    /**
     * Vérifie si l'utilisateur est connecté
     * @returns {Boolean} État de connexion
     */
    isAuthenticated() {
        return !!localStorage.getItem('auth_token')
    }

    /**
     * Récupère le token d'authentification
     * @returns {String|null} Token d'authentification
     */
    getToken() {
        return localStorage.getItem('auth_token')
    }

    /**
     * Sauvegarde les données d'authentification
     * @param {Object} authData Données d'authentification (token, user, company)
     */
    saveAuth(authData) {
        if (authData.access_token) {
            localStorage.setItem('auth_token', authData.access_token)
        }

        if (authData.user) {
            localStorage.setItem('user', JSON.stringify(authData.user))
        }

        if (authData.company) {
            localStorage.setItem('company', JSON.stringify(authData.company))
        }
    }

    /**
     * Supprime les données d'authentification
     */
    removeAuth() {
        localStorage.removeItem('auth_token')
        localStorage.removeItem('user')
        localStorage.removeItem('company')
        // Conserver device_id pour la réauthentification
    }
}

export default new AuthService()